import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getGameById, games } from '../data/games';
import Footer from '../sections/Footer';

const fadeUp   = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}`;
const gridAnim = keyframes`from{background-position:0 0}to{background-position:0 60px}`;
const pulse    = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;

const Page = styled.div`
  min-height: 100vh; overflow-x: hidden; background: #02020E; padding-top: 72px;
`;
const GridBg = styled.div`
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(rgba(0,163,255,.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,163,255,.02) 1px, transparent 1px);
  background-size: 60px 60px; animation: ${gridAnim} 22s linear infinite;
`;

/* ── Hero ── */
const Hero = styled.div`
  position: relative; z-index: 2;
  background: ${p => p.$gradient};
  padding: 80px 80px 60px;
  border-bottom: 1px solid rgba(255,255,255,.06);
  overflow: hidden;
  &::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(to right, rgba(2,2,14,.9) 40%, transparent 100%);
    pointer-events: none;
  }
  @media (max-width: 900px) { padding: 60px 24px 48px; }
  @media (max-width: 480px) { padding: 40px 16px 36px; }
`;
const HeroEmoji = styled.div`
  position: absolute; right: 8%; top: 50%; transform: translateY(-50%);
  font-size: clamp(100px, 14vw, 180px); opacity: .12; pointer-events: none;
  z-index: 1; user-select: none;
  @media (max-width: 700px) { display: none; }
`;
const HeroInner = styled.div`
  max-width: 1360px; margin: 0 auto; position: relative; z-index: 2;
`;
const BackBtnRow = styled.div`
  margin-bottom: 28px;
`;
const BackBtn = styled(Link)`
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: .22em;
  color: rgba(255,255,255,.4); text-decoration: none; text-transform: uppercase;
  transition: color .25s; display: inline-flex; align-items: center; gap: 6px;
  &:hover { color: ${p => p.$c || '#00A3FF'}; }
`;
const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: .26em;
  color: ${p => p.$c}; border: 1px solid ${p => p.$c}44; padding: 4px 12px;
  text-transform: uppercase; margin-bottom: 20px;
  &::before { content: ''; width: 5px; height: 5px; background: ${p => p.$c}; border-radius: 50%; animation: ${pulse} 2s infinite; }
`;
const GameTitle = styled.h1`
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(44px, 10vw, 120px); line-height: .88; letter-spacing: .04em;
  color: #fff; margin-bottom: 16px;
`;
const GameGenre = styled.p`
  font-family: 'Rajdhani', sans-serif; font-size: 14px; letter-spacing: .2em;
  color: rgba(255,255,255,.35); text-transform: uppercase; margin-bottom: 24px;
`;
const GameDesc = styled.p`
  font-family: 'Rajdhani', sans-serif; font-size: 16px; line-height: 1.8;
  color: rgba(255,255,255,.65); max-width: 560px; margin-bottom: 36px;
`;
const HeroBtns = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
`;
const PlayBtn = styled(Link)`
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 14px;
  letter-spacing: .2em; text-transform: uppercase; padding: 14px 36px;
  background: ${p => p.$c ? `linear-gradient(135deg, ${p.$c}, ${p.$c}99)` : 'linear-gradient(135deg,#00A3FF,#6B00FF)'};
  color: #fff; text-decoration: none;
  clip-path: polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  transition: all .3s;
  &:hover { filter: brightness(1.2); transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,0,0,.4); }
`;
const OutlineBtn = styled(Link)`
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 14px;
  letter-spacing: .2em; text-transform: uppercase; padding: 14px 36px;
  border: 1px solid rgba(255,255,255,.2); color: rgba(255,255,255,.6);
  text-decoration: none;
  clip-path: polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  transition: all .3s;
  &:hover { border-color: rgba(255,255,255,.5); color: #fff; background: rgba(255,255,255,.05); }
`;

/* ── Body ── */
const Body = styled.div`
  position: relative; z-index: 2;
  max-width: 1360px; margin: 0 auto;
  padding: 64px 80px 80px;
  display: grid; grid-template-columns: 1fr 340px; gap: 60px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
  @media (max-width: 900px) { padding: 40px 24px 60px; gap: 40px; }
  @media (max-width: 480px) { padding: 28px 16px 48px; }
`;

const MainCol = styled.div``;
const SideCol = styled.div``;

const SectionTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: .08em;
  color: #fff; margin-bottom: 20px;
`;
const LongDesc = styled.div`
  font-family: 'Rajdhani', sans-serif; font-size: 15px; line-height: 1.9;
  color: rgba(255,255,255,.6); margin-bottom: 48px;
  p { margin-bottom: 1em; }
`;
const FeatureList = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 48px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const FeatureItem = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 18px;
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.06);
  font-family: 'Rajdhani', sans-serif; font-size: 14px; letter-spacing: .06em;
  color: rgba(255,255,255,.6);
  &::before { content: '✦'; color: ${p => p.$c || '#00A3FF'}; font-size: 10px; flex-shrink: 0; }
`;

/* Sidebar card */
const SideCard = styled.div`
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.07);
  padding: 28px; margin-bottom: 20px;
`;
const SideRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,.06);
  &:last-child { border-bottom: none; }
`;
const SideKey = styled.span`
  font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: .18em;
  color: rgba(255,255,255,.3); text-transform: uppercase;
`;
const SideVal = styled.span`
  font-family: 'Rajdhani', sans-serif; font-size: 14px; font-weight: 600;
  color: rgba(255,255,255,.75); letter-spacing: .06em;
`;
const TagRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 7px; margin-top: 20px;
`;
const Tag = styled.span`
  font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: .14em;
  padding: 4px 10px; border: 1px solid rgba(255,255,255,.1);
  color: rgba(255,255,255,.4); text-transform: uppercase;
`;

/* More games */
const MoreSection = styled.div`
  position: relative; z-index: 2;
  max-width: 1360px; margin: 0 auto; padding: 0 80px 80px;
  @media (max-width: 900px) { padding: 0 24px 60px; }
`;
const MoreTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: .08em;
  color: #fff; margin-bottom: 24px;
`;
const MoreGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const MoreCard = styled(Link)`
  display: flex; align-items: center; gap: 16px; padding: 20px 24px;
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.06);
  text-decoration: none; transition: all .3s;
  &:hover { background: rgba(255,255,255,.06); border-color: rgba(255,255,255,.12); transform: translateY(-2px); }
`;
const MoreEmoji = styled.div`font-size: 32px; flex-shrink: 0;`;
const MoreInfo = styled.div``;
const MoreName = styled.div`font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:.06em;color:#fff;`;
const MoreGenre = styled.div`font-family:'Rajdhani',sans-serif;font-size:12px;letter-spacing:.14em;color:rgba(255,255,255,.35);`;

const NotFoundWrap = styled.div`
  position: relative; z-index: 2; padding-top: 72px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 60vh; text-align: center; gap: 20px;
  h1 { font-family: 'Bebas Neue', sans-serif; font-size: 60px; color: #fff; }
  p { color: rgba(255,255,255,.4); font-family: 'Rajdhani', sans-serif; font-size: 16px; }
`;

export default function GameDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const game = getGameById(id);

  if (!game) return (
    <Page>
      <GridBg />
      <NotFoundWrap>
        <h1>{t('gameDetail.notFound')}</h1>
        <p>{t('gameDetail.notFoundDesc')}</p>
        <OutlineBtn to="/games">{t('gameDetail.notFoundBack')}</OutlineBtn>
      </NotFoundWrap>
    </Page>
  );

  const others = games.filter(g => g.id !== game.id).slice(0, 3);

  return (
    <Page>
      <GridBg />

      <Hero $gradient={game.gradient}>
        <HeroEmoji>{game.emoji}</HeroEmoji>
        <HeroInner>
          <BackBtnRow>
            <BackBtn to="/games" $c={game.accentColor}>{t('gameDetail.backBtn')}</BackBtn>
          </BackBtnRow>
          <StatusBadge $c={game.statusColor}>{game.status}</StatusBadge>
          <GameTitle>{game.title}</GameTitle>
          <GameGenre>{game.genre} · {game.year}</GameGenre>
          <GameDesc>{game.description}</GameDesc>
          <HeroBtns>
            {game.webGame ? (
              <PlayBtn to={game.route} $c={game.accentColor}>{t('gameDetail.playNow')}</PlayBtn>
            ) : (
              <PlayBtn as="span" $c={game.accentColor} style={{opacity:.6,cursor:'default'}}>{t('gameDetail.inDevelopment')}</PlayBtn>
            )}
            <OutlineBtn to="/games">{t('gameDetail.viewAllGames')}</OutlineBtn>
          </HeroBtns>
        </HeroInner>
      </Hero>

      <Body>
        <MainCol>
          <SectionTitle>{t('gameDetail.aboutTitle')}</SectionTitle>
          <LongDesc>
            {game.longDescription.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
          </LongDesc>

          {game.features && <>
            <SectionTitle>{t('gameDetail.featuresTitle')}</SectionTitle>
            <FeatureList>
              {game.features.map((f, i) => (
                <FeatureItem key={i} $c={game.accentColor}>{f}</FeatureItem>
              ))}
            </FeatureList>
          </>}
        </MainCol>

        <SideCol>
          <SideCard>
            <SideRow>
              <SideKey>{t('gameDetail.sideStatus')}</SideKey>
              <SideVal style={{ color: game.statusColor }}>{game.status}</SideVal>
            </SideRow>
            <SideRow>
              <SideKey>{t('gameDetail.sidePlatform')}</SideKey>
              <SideVal>{game.platforms.join(', ')}</SideVal>
            </SideRow>
            <SideRow>
              <SideKey>{t('gameDetail.sideRelease')}</SideKey>
              <SideVal>{game.releaseDate || game.year}</SideVal>
            </SideRow>
            <SideRow>
              <SideKey>{t('gameDetail.sideDeveloper')}</SideKey>
              <SideVal>{game.developer}</SideVal>
            </SideRow>
            <TagRow>
              {game.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
            </TagRow>
          </SideCard>
        </SideCol>
      </Body>

      {others.length > 0 && (
        <MoreSection>
          <MoreTitle>{t('gameDetail.moreGames')}</MoreTitle>
          <MoreGrid>
            {others.map(g => (
              <MoreCard key={g.id} to={`/games/${g.id}`}>
                <MoreEmoji>{g.emoji}</MoreEmoji>
                <MoreInfo>
                  <MoreName>{g.title}</MoreName>
                  <MoreGenre>{g.genre}</MoreGenre>
                </MoreInfo>
              </MoreCard>
            ))}
          </MoreGrid>
        </MoreSection>
      )}

      <Footer />
    </Page>
  );
}
